import React from 'react';
import { DefaultDateTimeFormatter, intl } from '../../lib';
import { D3Tooltip } from './D3Tooltip';
import type { DoubleLineData, SingleLineData } from './LineChartUtil';

import {
  getValueWithUnit,
  isDoubleLineData,
  margin,
  getRangeIndicator,
  wrapTextToLines
} from './LineChartUtil';
import { formatMeasurementByLocale } from '../../util';

export type CommonLineChartTooltipProps = {
  data: SingleLineData | DoubleLineData | null;
  visible: boolean;
  position: {
    x: number;
    y: number;
  };
  firstLineLegend?: string;
  secondLineLegend?: string;
  constrainX?: number;
  hasDuplicateTimestamp?: boolean;
  // if disclaimerMessage is provided along with hasDuplicateTimestamp being true,
  // the tooltip will show the disclaimer message instead of data values
  disclaimerMessage?: string;
};

export type LineChartTooltipProps =
  | (CommonLineChartTooltipProps & {
      showHoverGuidelines: true;
      height: number;
      width: number;
    })
  | (CommonLineChartTooltipProps & {
      showHoverGuidelines?: false;
      height?: never;
      width?: never;
    });

export function LineChartTooltip({
  data,
  visible,
  position,
  firstLineLegend,
  secondLineLegend,
  constrainX,
  showHoverGuidelines,
  width,
  height,
  hasDuplicateTimestamp,
  disclaimerMessage
}: Readonly<LineChartTooltipProps>): React.JSX.Element {
  const firstLineValue = getValueWithUnit(
    data?.y.firstLine.value,
    data?.y.firstLine.unit
  );
  const isDoubleLine = isDoubleLineData(data);
  const secondLineValue =
    isDoubleLine &&
    getValueWithUnit(data.y.secondLine.value, data.y.secondLine.unit);

  const formattedDate = DefaultDateTimeFormatter.formatDate(data?.x ?? '');

  const isDisclaimer = !!hasDuplicateTimestamp && !!disclaimerMessage;
  const estimatedTextWidth = (disclaimerMessage?.length ?? 0) * 7;
  const disclaimerWidth = Math.max(200, Math.min(280, estimatedTextWidth + 40));

  const disclaimerLines = isDisclaimer
    ? wrapTextToLines(disclaimerMessage, disclaimerWidth)
    : [];

  const disclaimerHeight = 40 + disclaimerLines.length * 18;
  const disclaimerTitle = intl(
    'view.single.linechart.tooltip.measurmentsconflict.title'
  );

  return (
    <>
      {visible && showHoverGuidelines && (
        <>
          <line
            className="hover-dashed-line"
            x1={margin.left}
            x2={width + margin.left}
            y1={position.y}
            y2={position.y}
          />
          <line
            className="hover-dashed-line"
            x1={position.x}
            x2={position.x}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        </>
      )}
      <D3Tooltip
        visible={visible}
        position={position}
        className="line-chart-tooltip"
        width={
          isDisclaimer
            ? disclaimerWidth
            : // determine width depending on the data
              (isDoubleLine ? 70 : 50) +
              (data?.y.firstLine.rangeIndicator ? 12 : 0) +
              Math.max(
                formattedDate.length,
                data?.y.firstLine.optionalText?.length ?? 0,
                ((isDoubleLine &&
                  data.y.secondLine.optionalText?.length) as number) ?? 0,
                // to represent the padding between elements, we're adding 5 to the length
                ((firstLineLegend ?? '') + firstLineValue).length + 5,
                isDoubleLine
                  ? ((secondLineLegend ?? '') + secondLineValue).length + 5
                  : 0
              ) *
                6
        }
        height={isDisclaimer ? disclaimerHeight : isDoubleLine ? 122 : 96}
        constrainX={constrainX}
      >
        {isDisclaimer ? (
          <>
            <tspan
              dx="-4"
              dy="6"
              fontSize="32"
              style={{ fill: 'var(--functional-yellow)' }}
            >
              ●
            </tspan>
            <tspan dx="-11" dy="-5" fontSize="12" fontWeight="bold">
              !
            </tspan>
            <tspan
              dx="14"
              dy="1"
              textAnchor="start"
              className="graph-tooltip-value"
              fontWeight="bold"
            >
              {disclaimerTitle}
            </tspan>
            {disclaimerLines.map((line, index) => (
              <tspan
                key={index}
                x="0"
                dy={index === 0 ? '24' : '18'}
                textAnchor="start"
              >
                {line}
              </tspan>
            ))}
          </>
        ) : (
          data && (
            <>
              <tspan x="4" y="4" textAnchor="start">
                {firstLineLegend && (
                  <tspan>
                    <tspan className="graph-tooltip-colored-line-secondary">
                      ━
                    </tspan>
                    <tspan dx={10} className="graph-tooltip-value-description">
                      {firstLineLegend}
                      {': '}
                    </tspan>
                  </tspan>
                )}
                <tspan
                  dx={firstLineLegend ? 20 : 0}
                  className="graph-tooltip-value"
                >
                  {formatMeasurementByLocale(firstLineValue)}
                </tspan>
                {data.y.firstLine.rangeIndicator &&
                  getRangeIndicator(data.y.firstLine.rangeIndicator)}
              </tspan>

              {isDoubleLine && (
                <tspan x="4" dy="28" textAnchor="start">
                  <tspan>
                    <tspan className="graph-tooltip-colored-line-primary">
                      ━
                    </tspan>
                    <tspan dx={10} className="graph-tooltip-value-description">
                      {secondLineLegend}
                      {': '}
                    </tspan>
                  </tspan>
                  <tspan dx={20} className="graph-tooltip-value">
                    {secondLineValue !== false
                      ? formatMeasurementByLocale(secondLineValue)
                      : secondLineValue}
                  </tspan>
                </tspan>
              )}

              <tspan x="4" dy="28" textAnchor="start">
                {formattedDate}
              </tspan>
              <tspan x="4" dy="28" textAnchor="start">
                {isDoubleLine
                  ? data.y.firstLine.optionalText ||
                    data.y.secondLine.optionalText
                  : data.y.firstLine.optionalText}
              </tspan>
            </>
          )
        )}
      </D3Tooltip>
    </>
  );
}
